import type { IVisit, IVisitState } from '@app/domain/visit/visit.interface'
import { convertToUTC } from '@app/lib/time'
import { authenticatedRequest } from '@app/rest/rest.lib'
import type { IApiPatient } from '@app/state/patients/helpers'

import type {
  IApiEntry,
  IApiUpdateVisit,
  IApiVisit,
  IApiVisitDefinition,
} from './helpers'

export interface GetVisitResponse {
  visit: {
    visit: IApiVisit
    patient?: IApiPatient
  }
}

export function getVisitById(id: string) {
  return authenticatedRequest<GetVisitResponse>({
    method: 'get',
    url: `/api/v2/visit/${id}`,
  })
}

export interface GetVisitListResponse {
  visits: Array<{
    visit: IApiVisit
    patient?: IApiPatient
  }>
}

export function getVisits() {
  return authenticatedRequest({
    method: 'get',
    url: `/api/v2/visits`,
  })
}

export interface CreateVisitResponse {
  visit: IApiVisit
}

export interface RetrieveVisitEntriesResponse {
  entries: Array<IApiEntry>
}

export function retrieveVisitEntries(visitId: string) {
  return authenticatedRequest<RetrieveVisitEntriesResponse>({
    method: 'get',
    url: `/api/v2/visit/${visitId}/entries`,
  })
}

export interface RetrieveVisitEntryResponse {
  entry: IApiEntry
}

export function retrieveVisitEntryForInstrument(
  visitId: string,
  instrumentId: string,
  instrumentVersion: number,
) {
  return authenticatedRequest<RetrieveVisitEntryResponse>({
    method: 'get',
    url: `/api/v2/visit/${visitId}/instrument/${instrumentId}/entry?instrumentVersion=${instrumentVersion}`,
  })
}

export function createVisit(
  patientId: string,
  definitionId: string,
  startTime: string,
  location: string,
) {
  // When creating a visit, we assume that the startTime value being sent to this function is in the browser's local timezone
  // The API expects all times to be in UTC, so the startTime needs to be converted to UTC.
  const startTimeUTC = convertToUTC(startTime)
  return authenticatedRequest({
    method: 'post',
    url: `/api/v2/visit`,
    data: {
      patientId: patientId,
      definitionId: definitionId,
      startTime: startTimeUTC,
      location: location,
      visitStatus: 'not-started',
    },
  })
}

export interface UpdateVisitResponse {
  visit: IVisit
}

export function updateVisit(visitId: string, updatedVisit: IApiUpdateVisit) {
  const startTimeUTC = convertToUTC(updatedVisit.startTime)
  return authenticatedRequest({
    method: 'post',
    url: `/api/v2/visit/${visitId}`,
    data: {
      definitionId: updatedVisit.definitionId,
      location: updatedVisit.location,
      startTime: startTimeUTC,
      visitStatus: updatedVisit.visitStatus,
    },
  })
}

export interface UpdateStateResponse {
  visit: IApiVisit
}

export function updateState(visitId: string, state: IVisitState) {
  return authenticatedRequest({
    method: 'post',
    url: `/api/v2/visit/${visitId}/state`,
    data: {
      state: {
        instrument: state.instrumentId,
        screen: state.screen,
        stage: state.stage,
      },
    },
  })
}

export interface RetrieveVisitDefinitionResponse {
  definition: IApiVisitDefinition
}

export function retrieveVisitDefinition(visitId: string) {
  return authenticatedRequest({
    method: 'get',
    url: `/api/v2/visit/${visitId}/definition`,
  })
}

export interface AddInstrumentToVisitResponse {
  status: string
}

export function addInstrumentToVisit(visitId: string, instrumentId: string) {
  return authenticatedRequest({
    method: 'post',
    url: `/api/v2/visit/${visitId}/add-instrument`,
    data: {
      instrumentId: instrumentId,
    },
  })
}

export interface RetrieveDefinitionListResponse {
  definitions: Array<IApiVisitDefinition>
}

export function retrieveDefinitionList(location?: string) {
  return authenticatedRequest<RetrieveDefinitionListResponse>({
    method: 'get',
    url: `/api/v2/definitions?location=${location}`,
  })
}

export function requestEntryPDF(entryId: string, templateId: string) {
  return authenticatedRequest({
    method: 'get',
    responseType: 'blob',
    url: `/api/v2/entry/${entryId}/${templateId}/authenticatedPDF`,
  })
}

export function getDocumentFileName(
  fromContentDisposition: undefined | string,
  fallbackName: string,
  format: string,
): string {
  const matcher = new RegExp(/filename="(.+)"/i)
  const matchResult = matcher.exec(fromContentDisposition)
  if (matchResult !== null && matchResult.length === 2) {
    return matchResult[1]
  } else {
    return `${fallbackName}.${format}`
  }
}
